import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Monitor, Power, Clock, Calendar, Play, Cpu, MemoryStick, HardDrive } from 'lucide-react';
import api from '../../services/api';
import { useToast } from '../../contexts/ToastContext';

type VM = any;

// Interface para resposta paginada
interface PaginatedResponse {
  data: VM[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// Interface para resposta paginada de ambientes
interface PaginatedAmbienteResponse {
  data: any[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// Função auxiliar para formatar RAM (MB para GB)
const formatRam = (ramInMB: number | null | undefined): string => {
  if (typeof ramInMB !== 'number') return '-';
  return `${(ramInMB / 1024).toFixed(1)} GB`;
};

export const Dashboard = () => {
  const [vms, setVMs] = useState<VM[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [ambientes, setAmbientes] = useState<Record<string, string>>({});
  const { addToast } = useToast();

  useEffect(() => {
    Promise.all([fetchVMs(), fetchAmbientes()]).finally(() => {
      setIsLoading(false);
    });
  }, []);

  const fetchVMs = async () => {
    try {
      const response = await api.get<PaginatedResponse>('/api/vms');
      setVMs(response.data.data);
    } catch (error: any) {
      console.error('Error fetching VMs:', error);
      addToast({ type: 'error', title: 'Erro ao buscar VMs', description: error.message });
    }
  };

  const fetchAmbientes = async () => {
    try {
      const response = await api.get<PaginatedAmbienteResponse>('/api/ambientes');
      const ambientesMap = response.data.data.reduce((acc: Record<string, string>, ambiente: any) => {
        acc[ambiente.id] = ambiente.nome;
        return acc;
      }, {});
      setAmbientes(ambientesMap);
    } catch (error: any) {
      console.error('Error fetching ambientes:', error);
      addToast({ type: 'error', title: 'Erro ao buscar Ambientes', description: error.message });
    }
  };

  const getStateLabel = (state: string) => {
    const labels: Record<string, string> = {
      'running': 'Em execução',
      'stopped': 'Parada',
      'starting': 'Iniciando',
      'stopping': 'Parando',
      'unknown': 'Desconhecido'
    };
    return labels[state] || state;
  };

  const getStateColor = (state: string) => {
    const colors: Record<string, string> = {
      'running': 'bg-green-100 text-green-800',
      'stopped': 'bg-red-100 text-red-800',
      'starting': 'bg-yellow-100 text-yellow-800',
      'stopping': 'bg-yellow-100 text-yellow-800',
      'unknown': 'bg-gray-100 text-gray-800'
    };
    return colors[state] || 'bg-gray-100 text-gray-800';
  };

  const getCardBackgroundColor = (state: string) => {
    const colors: Record<string, string> = {
      'running': 'bg-green-100',
      'stopped': 'bg-red-100',
      'starting': 'bg-yellow-100',
      'stopping': 'bg-yellow-100',
      'unknown': 'bg-gray-100'
    };
    return colors[state] || 'bg-gray-100';
  };

  const translateWeekday = (day: string) => {
    const translations: Record<string, string> = {
      'mon': 'Seg', 
      'tue': 'Ter',
      'wed': 'Qua',
      'thu': 'Qui',
      'fri': 'Sex',
      'sat': 'Sáb',
      'sun': 'Dom'
    };
    return translations[day.toLowerCase()] || day;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <Link
          to="/vms"
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Monitor className="w-5 h-5 mr-2" />
          Ver todas as VMs
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {vms
          .filter(vm => vm.ativo)
          .map((vm) => (
            <div key={vm.id} className={`relative rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow ${getCardBackgroundColor(vm.state || 'unknown')}`}>
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">{vm.nome}</h2>
                  <div className="text-sm text-gray-500 flex flex-col mt-1">
                    <div className="flex items-center flex-wrap gap-x-3 gap-y-1 font-medium text-gray-600">
                      <span className="flex items-center" title="CPU">
                         <Cpu className="w-4 h-4 mr-1 text-gray-500" />
                         {vm.tipoVm?.vcpus || '-'} 
                      </span>
                      <span className="flex items-center" title="RAM">
                         <MemoryStick className="w-4 h-4 mr-1 text-gray-500" />
                         {formatRam(vm.tipoVm?.ram)}
                      </span>
                      <span className="flex items-center" title="Disco">
                         <HardDrive className="w-4 h-4 mr-1 text-gray-500" />
                         {vm.tipoVm?.disk ? `${vm.tipoVm.disk}GB` : '-'}
                      </span>
                    </div>
                    <span>
                       {ambientes[vm.ambiente_id] || '-'} - {vm.regiao}
                    </span>
                  </div>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStateColor(vm.state || 'unknown')}`}>
                  {getStateLabel(vm.state || 'unknown')}
                </span>
              </div>

              <div className="flex items-center justify-between flex-wrap gap-x-4 gap-y-2 mt-4 text-gray-600">
                  <div className="flex items-center" title={`Horário de Início: ${vm.start_time || 'Não definido'}`}>
                    <Play className="w-5 h-5 mr-1 text-green-600" />
                    <span>
                      {vm.start_time || '-'}
                    </span>
                  </div>
                  <div className="flex items-center" title={`Horário de Parada: ${vm.stop_time || 'Não definido'}`}>
                    <Power className="w-5 h-5 mr-1 text-red-600" />
                    <span>
                      {vm.stop_time || '-'}
                    </span>
                  </div>
                  <div className="flex items-center text-xs" title="Dias Agendados">
                    <Calendar className="w-4 h-4 mr-1" />
                    <span>
                      {vm.weekdays && vm.weekdays.length > 0
                        ? vm.weekdays.map((day: string) => translateWeekday(day)).join(', ')
                        : 'Nenhum'}
                    </span>
                  </div>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}; 